/**
 * Armature Component Prototype
 */

let getUUID;
{
	let uuid = 0;

	getUUID = () => ++uuid;
}

type ArmTemplate = (component: Component) => string;

const Template = (template: ArmTemplate): ClassDecorator => {
	return (target: typeof Component) => {
		target.prototype.$template = template;
	};
};

export {Template};

const typeMap = new Map<string, typeof Component>();

const TagName = (name: string): ClassDecorator => {
	return (target: typeof Component) => {
		target.$tagName = name;
		typeMap.set(name, target);
	};
};

export {TagName};

const ClassName = (name: string): ClassDecorator => {
	return (target: typeof Component) => {
		target.$classNames.push(name);
	};
};

export {ClassName};

interface DeflatedComponent {
	type: any;
	params: any;
	id: number;
	label: any;
	children?: DeflatedComponent[];
}

@TagName("raven-component")
@Template(() => "")
export default class Component {
	$element: HTMLElement;
	$children: Component[] = [];
	$parent: Component;
	$id: number = getUUID();
	$label: any;

	static $classNames: string[] = [];
	static $tagName: string;
	$template: ArmTemplate;

	protected $params: any;

	constructor(params: any, target?: HTMLElement) {
		this.$params = params;
		this.$element = target;
	}

	$deflate(): DeflatedComponent {
		let thisClass = (<typeof Component>this.constructor);

		return {
			type: thisClass.$tagName,
			params: this.$params,
			id: this.$id,
			label: this.$label,
			children: this.$children.map(v => v.$deflate())
		};
	}

	static $inflate(data: DeflatedComponent, inst?: Component) {
		let thisClass = typeMap.get(data.type) || this;

		if (!inst) {
			inst = new thisClass(data.params);
		}

		inst.$id = data.id;
		inst.$label = data.label;

		if (data.children) {
			for (let child of data.children) {
				let thatClass = typeMap.get(child.type) || this;
				let childInst = thatClass.$for(inst, child.label, child.params);
				thatClass.$inflate(child, childInst);
			}
		}

		return inst;
	}

	static $getIdentifier(label: any) {
		return this.$tagName + label;
	}

	$getIdentifier() {
		const thisClass = <typeof Component>this.constructor;

		return thisClass.$tagName + this.$label;
	}

	static $for(parent: Component, label: any, params: any) {
		const identifier = this.$getIdentifier(label);
		const existing = parent.$children.find(v => v.$getIdentifier() === identifier);

		if (existing) {
			return existing;
		}

		const inst = new this(params);
		inst.$label = label;
		inst.$parent = parent;

		parent.$children.push(inst);

		return inst;
	}

	$appendTo(parent: HTMLElement) {
		this.$ensureElement();

		parent.appendChild(this.$element);
	}

	$query(selector: string) {
		return <HTMLElement>this.$element.querySelector(selector);
	}

	$querys(selector: string) {
		return <HTMLElement[]>Array.from(this.$element.querySelectorAll(selector));
	}

	$ensureElement() {
		if (!this.$element) {
			const thisClass = <typeof Component>this.constructor;

			const el = this.$element = document.createElement(thisClass.$tagName);
			el.setAttribute("data-id", this.$id.toString());
			el.setAttribute("data-label", this.$label);
		}
	}

	$locate(parent: HTMLElement = document.body) {
		const thisClass = <typeof Component>this.constructor;

		let el = <HTMLElement>parent.querySelector(`${ thisClass.$tagName }[data-id="${ this.$id }"]`);

		if (el && this.$element !== el) {
			this.$element = el;

			return el;
		}
	}

	toString() {
		return this.$getHTML();
	}

	$getHTML() {
		const thisClass = <typeof Component>this.constructor;

		return `<${ thisClass.$tagName } class="${ thisClass.$classNames.join(" ") }" data-id="${ this.$id }" data-label="${ this.$label }">${ this.$template(this) }</${ thisClass.$tagName }>`;
	}

	$render() {
		this.$element.innerHTML = this.$template(this);
	}

	$hydrate() {
		for (let child of this.$children) {
			if (child.$locate(this.$element)) {
				child.$hydrate();
			}
		}
	}

	$bind() {
		this.$ensureElement();
		this.$render();
		this.$hydrate();
	}
}